import { useEffect, useState , useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Fingerprint, Lock, ShieldCheck, Sparkles, ArrowRight, KeyRound,
  Receipt, Wallet, Shield, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

import api, { formatApiErrorDetail, explainError, hardReset } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Particles from "@/components/Particles";
import AnimatedHand from "@/components/AnimatedHand";
import PinPad from "@/components/PinPad";
import QuickBillModal from "@/components/QuickBillModal";
import QuickExpenseModal from "@/components/QuickExpenseModal";
import { AUTH } from "@/constants/testIds";

/**
 * KIOSK LANDING
 * -------------
 * Tap hand → shows 2 primary options (Bill / Expense) + smaller Admin link.
 * Bill/Expense: PIN-only (4-digit) → open respective modal → close → back to landing (logout).
 * Admin: full password/PIN auth popup → dashboard (persistent session).
 */
export default function LoginPage() {
  const nav = useNavigate();
  const { user, loading, loginWithPassword, loginWithPin, verifyLoginOtp, resendLoginOtp, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pinMode, setPinMode] = useState(null); // null | "bill" | "expense"
  const [pinError, setPinError] = useState("");
  const [showBill, setShowBill] = useState(false);
  const [showExp, setShowExp] = useState(false);
  const [showAdminAuth, setShowAdminAuth] = useState(false);
  const [forgot, setForgot] = useState(0);
  const [loginOtpStep, setLoginOtpStep] = useState(false);
  const [loginOtpToken, setLoginOtpToken] = useState("");
  const [loginOtpChannel, setLoginOtpChannel] = useState("email");
  const [loginOtpSentTo, setLoginOtpSentTo] = useState("");
  const [loginOtp, setLoginOtp] = useState("");
  const [otpCooldown, setOtpCooldown] = useState(0);
  useEffect(() => {
    if (otpCooldown <= 0) return undefined;
    const _t = setTimeout(() => setOtpCooldown((n) => n - 1), 1000);
    return () => clearTimeout(_t);
  }, [otpCooldown]);          // 0=off 1=email 2=otp
  const [fOtp, setFOtp] = useState("");
  const [fPw, setFPw] = useState("");
  const [fPin, setFPin] = useState("");
  const [fMsg, setFMsg] = useState("");
  const [fBusy, setFBusy] = useState(false);
  const handSize = typeof window !== "undefined" && window.innerWidth < 640 ? 232 : 360;

  // Admin auth popup state
  const [adminTab, setAdminTab] = useState("password");
  const [email, setEmail] = useState("");
  const [book, setBook] = useState(() => { try { return localStorage.getItem("it_book") === "gst" ? "gst" : "main"; } catch (e) { return "main"; } });
  const [password, setPassword] = useState("Owner@2026");
  const [adminPin, setAdminPin] = useState("");
  const submitAdminPinRef = useRef(null);
  const [rememberDevice, setRememberDevice] = useState(true);
  const [adminError, setAdminError] = useState("");
  // token khatam hone par yahan bheja jata hai — banda ko wajah pata chale
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("expired") === "1") {
      setAdminError("Session expired — please sign in again");
      window.history.replaceState({}, "", "/login");
    }
  }, []);
  const [errHint, setErrHint] = useState("");
  const [netFail, setNetFail] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // If admin session exists → go to dashboard directly (only if entered via Admin login)
    if (!loading && user && localStorage.getItem("it_admin_session") === "1") {
      nav("/", { replace: true });
    }
  }, [user, loading, nav]);

  const openBillFlow = () => { setMenuOpen(false); setPinMode("bill"); setPinError(""); };
  const openExpFlow = () => { setMenuOpen(false); setPinMode("expense"); setPinError(""); };
  const openAdminAuth = () => { setMenuOpen(false); setShowAdminAuth(true); };

  // Called when 4-digit PIN entered
  const verifyPin = async (pin) => {
    setPinError("");
    try {
      const { data } = await api.post("/auth/pin-only-login", { pin });
      localStorage.setItem("it_token", data.token);
      localStorage.removeItem("it_admin_session"); // this is quick-entry, not admin
      const mode = pinMode;
      setPinMode(null);
      if (mode === "bill") setShowBill(true);
      else if (mode === "expense") setShowExp(true);
    } catch (e) {
      const x = explainError(e);
      setErrHint(x.hint); setNetFail(x.network);
      const msg = x.msg || "Wrong PIN";
      setPinError(msg);
      throw e;
    }
  };

  const closeQuick = () => {
    setShowBill(false); setShowExp(false);
    // Clear session — force back to landing on next action
    localStorage.removeItem("it_token");
    localStorage.removeItem("it_admin_session");
    toast.success("Signed out", { description: "Session cleared for security" });
  };

  const submitAdminPassword = async (e) => {
    e?.preventDefault?.();
    setAdminError(""); setSubmitting(true);
    try {
      const _r = await loginWithPassword(email, password, rememberDevice);
      if (_r && _r.otpRequired) {
        setLoginOtpToken(_r.otpToken); setLoginOtpChannel(_r.channel);
        setLoginOtpSentTo(_r.sentTo); setLoginOtp(""); setLoginOtpStep(true); setOtpCooldown(45);
        setSubmitting(false); return;
      }
      localStorage.setItem("it_admin_session", "1");
      toast.success("Welcome to admin dashboard");
      setTimeout(() => nav("/", { replace: true }), 200);
    } catch (err) {
      const x = explainError(err);
      setAdminError(x.msg); setErrHint(x.hint); setNetFail(x.network);
    } finally { setSubmitting(false); }
  };

  const verifyLoginOtpSubmit = async (e, codeArg) => {
    e?.preventDefault?.();
    const code = String(codeArg != null ? codeArg : loginOtp);
    setAdminError(""); setErrHint(""); setNetFail(false);
    if (code.length !== 6) { setAdminError("Enter the 6-digit code"); return; }
    setSubmitting(true);
    try {
      await verifyLoginOtp(loginOtpToken, code, rememberDevice, email);
      localStorage.setItem("it_admin_session", "1");
      toast.success("Welcome to admin dashboard");
      setTimeout(() => nav("/", { replace: true }), 200);
    } catch (err) {
      const x = explainError(err);
      setAdminError(x.msg); setErrHint(x.hint); setNetFail(x.network);
    } finally { setSubmitting(false); }
  };

  const resendLoginOtpClick = async () => {
    setAdminError(""); setErrHint("");
    try {
      const d = await resendLoginOtp(loginOtpToken);
      setLoginOtpChannel(d.channel); setLoginOtpSentTo(d.sent_to); setLoginOtp("");
      toast.success("New code sent"); setOtpCooldown(45);
    } catch (err) {
      const x = explainError(err);
      setAdminError(x.msg);
    }
  };

  const sendOtp = async () => {
    if (!email.trim()) { setFMsg("Enter email first"); return; }
    setFBusy(true); setFMsg("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setFMsg(data.message || "Code bhej diya");
      setForgot(2);
    } catch (err) {
      setFMsg(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't send the code");
    } finally { setFBusy(false); }
  };

  const doReset = async () => {
    if (fOtp.length !== 6) { setFMsg("Enter the 6-digit code"); return; }
    if (!fPw && !fPin) { setFMsg("Enter either a new password or PIN"); return; }
    setFBusy(true); setFMsg("");
    try {
      await api.post("/auth/reset-password", { email: email.trim(), otp: fOtp, new_password: fPw, new_pin: fPin });
      setFMsg("✅ Done! Now log in with the new password.");
      setPassword(""); setFOtp(""); setFPw(""); setFPin("");
      setTimeout(() => { setForgot(0); setFMsg(""); }, 2500);
    } catch (err) {
      setFMsg(formatApiErrorDetail(err?.response?.data?.detail) || "Reset fail");
    } finally { setFBusy(false); }
  };

  const submitAdminPin = async (e, pinArg) => {
    e?.preventDefault?.();
    const pin = String(pinArg || adminPin || "");
    setAdminError(""); setErrHint(""); setNetFail(false);
    if (pin.length !== 4) { setAdminError("Enter your 4-digit PIN"); return; }
    setSubmitting(true);
    try {
      // Only PIN se — email ki zaroorat nahi. PIN khud bata deta hai kaun hai.
      const { data } = await api.post("/auth/pin-only-login", { pin });
      localStorage.setItem("it_token", data.token);
      localStorage.setItem("it_admin_session", "1");
      toast.success(`Welcome, ${data?.user?.name || ""}`.trim());
      setTimeout(() => { window.location.replace("/"); }, 150);
      return;
    } catch (err) {
      // PIN-only na chala to purana tareeka (email + PIN) bhi try kar lo
      if (email && String(email).includes("@")) {
        try {
          await loginWithPin(email, pin);
          localStorage.setItem("it_admin_session", "1");
          toast.success("Welcome");
          setTimeout(() => nav("/", { replace: true }), 200);
          return;
        } catch { /* neeche asli error dikhayenge */ }
      }
      const x = explainError(err);
      setAdminError(x.msg); setErrHint(x.hint); setNetFail(x.network);
    } finally { setSubmitting(false); }
  };

  submitAdminPinRef.current = (p) => submitAdminPin(null, p);

  return (
    <div className="relative min-h-screen w-full overflow-hidden grain" style={{ background: "#030305" }}>
      {/* Aurora */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1715538859909-914de12746b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxkYXJrJTIwc3BhY2UlMjBhdXJvcmF8ZW58MHx8fHwxNzg1MDc4ODQxfDA&ixlib=rb-4.1.0&q=60&w=1600&auto=format')",
          backgroundSize: "cover", backgroundPosition: "center", opacity: 0.42,
        }}
      />
      <div className="aurora-bg" />
      <Particles density={typeof window !== "undefined" && window.innerWidth < 640 ? 35 : 80} />

      <motion.div className="orb" style={{ width: 340, height: 340, top: "8%", left: "8%", background: "#00F0FF" }}
        animate={{ y: [0, 30, 0], x: [0, 20, 0] }} transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div className="orb" style={{ width: 420, height: 420, bottom: "-6%", right: "6%", background: "#7000FF" }}
        animate={{ y: [0, -30, 0], x: [0, -20, 0] }} transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }} />

      {/* Brand bar */}
      <div className="absolute top-0 inset-x-0 z-40 flex items-center justify-between px-4 py-4 sm:px-8 sm:py-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl pill-neon flex items-center justify-center">
            <Sparkles size={16} className="text-cyan-300" />
          </div>
          <div className="leading-tight">
            <p className="display text-[15px] tracking-tight">Interior Traders</p>
            <p className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">decor.interiortraders.in</p>
          </div>
        </div>
        <button
          data-testid="admin-link"
          onClick={openAdminAuth}
          className="glass rounded-full px-4 py-2 flex items-center gap-2 text-[12.5px] hover:border-cyan-400/40 transition-colors"
        >
          <Shield size={13} className="text-cyan-300" />
          Admin dashboard
          <ChevronRight size={12} className="text-white/50" />
        </button>
      </div>

      {/* Hero */}
      <div className="relative z-30 min-h-screen flex flex-col items-center justify-center px-6">
        <AnimatePresence mode="wait">
          {!menuOpen ? (
            <motion.div
              key="hero"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.94 }}
              transition={{ duration: 0.6 }}
              className="flex flex-col items-center gap-6 sm:gap-8 text-center"
            >
              <motion.p
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                className="mono text-[11px] uppercase tracking-[0.36em] text-white/50"
              >
                Interior Traders · Smart ERP
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
                className="display text-4xl sm:text-5xl md:text-7xl max-w-3xl px-2" style={{ lineHeight: 0.98 }}
              >
                Tap the hand.{" "}
                <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(135deg,#00F0FF,#7000FF 60%,#FF2E93)" }}>
                  Bill or Expense.
                </span>
              </motion.h1>

              <AnimatedHand size={handSize} testId={AUTH.handTrigger} onClick={() => setMenuOpen(true)} />

              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
                className="flex items-center gap-3 mono text-[11px] text-white/55"
              >
                <span className="dot" />
                Tap the hand · 4-digit PIN · Instant entry
              </motion.div>
            </motion.div>
          ) : (
            <motion.div
              key="menu"
              initial={{ opacity: 0, y: 20, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.96 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-2xl"
              data-testid="quick-menu"
            >
              <div className="text-center mb-8">
                <p className="mono text-[10px] uppercase tracking-[0.3em] text-white/50">Choose entry</p>
                <h2 className="display text-3xl sm:text-4xl mt-2">What are you recording?</h2>
                <button onClick={() => setMenuOpen(false)} className="mt-3 mono text-[11px] text-white/55 hover:text-white">← back</button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <motion.button
                  data-testid="quick-choice-bill"
                  onClick={openBillFlow}
                  whileHover={{ scale: 1.02, y: -3 }}
                  whileTap={{ scale: 0.98 }}
                  className="glass-strong rounded-3xl p-6 sm:p-8 text-left relative overflow-hidden group"
                >
                  <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl" style={{ background: "rgba(0,240,255,0.32)" }} />
                  <div className="relative">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center">
                      <Receipt size={22} className="text-black" />
                    </div>
                    <h3 className="display text-2xl sm:text-3xl mt-5 sm:mt-6">New bill</h3>
                    <p className="mono text-[11.5px] text-white/55 mt-2 uppercase tracking-[0.18em]">Non-GST · Cash memo</p>
                    <p className="text-[13px] text-white/70 mt-3">Fast checkout with product search, any payment mode, instant print & PDF.</p>
                    <div className="mt-6 flex items-center gap-2 mono text-[11px] text-cyan-300">
                      <KeyRound size={12} /> PIN protected <ArrowRight size={12} />
                    </div>
                  </div>
                </motion.button>

                <motion.button
                  data-testid="quick-choice-expense"
                  onClick={openExpFlow}
                  whileHover={{ scale: 1.02, y: -3 }}
                  whileTap={{ scale: 0.98 }}
                  className="glass-strong rounded-3xl p-6 sm:p-8 text-left relative overflow-hidden group"
                >
                  <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl" style={{ background: "rgba(255,46,147,0.3)" }} />
                  <div className="relative">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center">
                      <Wallet size={22} className="text-black" />
                    </div>
                    <h3 className="display text-2xl sm:text-3xl mt-5 sm:mt-6">New expense</h3>
                    <p className="mono text-[11.5px] text-white/55 mt-2 uppercase tracking-[0.18em]">15 categories · 1-tap</p>
                    <p className="text-[13px] text-white/70 mt-3">Rent, electricity, salary, petrol, tea, marketing — categorised in seconds.</p>
                    <div className="mt-6 flex items-center gap-2 mono text-[11px] text-pink-300">
                      <KeyRound size={12} /> PIN protected <ArrowRight size={12} />
                    </div>
                  </div>
                </motion.button>
              </div>

              <div className="mt-8 text-center">
                <button
                  data-testid="admin-link-inline"
                  onClick={openAdminAuth}
                  className="mono text-[11px] uppercase tracking-[0.28em] text-white/45 hover:text-cyan-300 flex items-center gap-2 mx-auto"
                >
                  <Shield size={12} /> Admin dashboard → full ERP
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bottom taglines */}
      <div className="absolute bottom-4 sm:bottom-8 inset-x-0 z-30 flex flex-wrap items-center justify-center gap-3 sm:gap-6 px-4 text-center mono text-[10px] uppercase tracking-[0.28em] text-white/35">
        <span>Guwahati · Assam</span>
        <span className="dot" />
        <span>v2.0</span>
      </div>

      {/* PIN pad for Bill/Expense */}
      <PinPad
        open={pinMode !== null}
        title="Enter your 4-digit PIN"
        subtitle={pinMode === "bill" ? "Unlock new bill" : "Unlock new expense"}
        onClose={() => { setPinMode(null); setPinError(""); }}
        onSubmit={verifyPin}
        error={pinError}
        testId="quick-pin"
      />

      {/* Quick modals */}
      <QuickBillModal open={showBill} onClose={closeQuick} onCreated={() => {}} />
      <QuickExpenseModal open={showExp} onClose={closeQuick} onCreated={() => {}} />

      {/* Admin auth popup */}
      <AnimatePresence>
        {showAdminAuth && (
          <div
            className="fixed inset-0 z-[110] flex items-center justify-center px-4"
            style={{ background: "rgba(3,3,5,0.7)", backdropFilter: "blur(14px)" }}
            onClick={() => setShowAdminAuth(false)}
          >
            <div
              data-testid={AUTH.authPopup}
              onClick={(e) => e.stopPropagation()}
              className="glass-strong w-full max-w-md rounded-3xl p-6 sm:p-8 relative overflow-hidden"
            >
              <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(0,240,255,0.16)" }} />
              <div className="absolute -bottom-24 -left-24 w-56 h-56 rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(112,0,255,0.18)" }} />

              <div className="flex items-center justify-between mb-6 relative">
                <div>
                  <p className="mono text-[10px] uppercase tracking-[0.3em] text-white/50">Admin sign-in</p>
                  <h2 className="display text-2xl mt-1">Full ERP dashboard</h2>
                </div>
                <button
                  onClick={() => setShowAdminAuth(false)}
                  className="w-9 h-9 rounded-full glass flex items-center justify-center hover:border-white/30"
                  data-testid="admin-auth-back"
                >
                  <span className="mono text-white/60 text-xs">esc</span>
                </button>
              </div>

              

              {loginOtpStep ? (
                <div className="space-y-4 relative">
                  <div className="text-center">
                    <div className="mono text-[11px] uppercase tracking-[0.24em] text-white/50">Verify it's you</div>
                      <div className={"inline-block mt-2 px-3 py-1 rounded-full mono text-[11px] uppercase tracking-[0.2em] " + (book === "gst" ? "bg-emerald-400/20 text-emerald-200" : "bg-cyan-400/20 text-cyan-200")}>{book === "gst" ? "GST book" : "Cash book"}</div>
                    <div className="text-white/70 text-[13px] mt-2">
                      Code {loginOtpChannel === "whatsapp" ? "sent on WhatsApp to" : "emailed to"} {loginOtpSentTo}
                    </div>
                  </div>
                  <form onSubmit={verifyLoginOtpSubmit} className="space-y-4">
                    <div>
                      <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">6-digit code</label>
                      <input
                        value={loginOtp}
                        onChange={(e) => { const _v = e.target.value.replace(/[^0-9]/g, "").slice(0, 6); setLoginOtp(_v); if (_v.length === 6 && !submitting) verifyLoginOtpSubmit(null, _v); }}
                        inputMode="numeric" autoComplete="one-time-code" autoFocus
                        className="mt-2 w-full px-4 py-3.5 rounded-xl bg-white/[0.03] border border-white/10 text-white text-center tracking-[0.6em] mono text-xl outline-none focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/20 transition"
                        placeholder="______" />
                    </div>
                    {adminError && (
                      <div className="rounded-xl px-3.5 py-3 bg-rose-400/10 border border-rose-400/25">
                        <div className="text-[13px] text-rose-300 mono">{adminError}</div>
                      </div>
                    )}
                    <button type="submit" disabled={submitting}
                      className="w-full px-4 py-4 rounded-xl bg-white text-black mono text-[13px] font-medium disabled:opacity-60">
                      {submitting ? "Checking..." : "Verify & enter"}
                    </button>
                    <div className="flex items-center justify-between text-[12px] mono pt-1">
                      <button type="button" onClick={() => { setLoginOtpStep(false); setAdminError(""); setErrHint(""); }} className="text-white/50 hover:text-white/70">Back</button>
                      <button type="button" onClick={resendLoginOtpClick} disabled={otpCooldown > 0} className="text-cyan-300 hover:text-cyan-200 disabled:text-white/30 disabled:cursor-not-allowed">{otpCooldown > 0 ? `Resend in ${otpCooldown}s` : "Resend code"}</button>
                    </div>
                  </form>
                </div>
                ) : forgot ? (
                <div className="space-y-4 relative">
                  <div>
                    <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">Aapka email</label>
                    <input
                      type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      disabled={forgot === 2}
                      className="mt-2 w-full px-4 py-3.5 rounded-xl bg-white/[0.03] border border-white/10 text-white outline-none focus:border-cyan-400/50 disabled:opacity-60"
                    />
                  </div>

                  {forgot === 1 && (
                    <button onClick={sendOtp} disabled={fBusy}
                      className="w-full py-3.5 rounded-xl bg-white text-black font-semibold text-[14px] disabled:opacity-60">
                      {fBusy ? "Sending…" : "Send a 6-digit code by Email"}
                    </button>
                  )}

                  {forgot === 2 && (
                    <>
                      <div>
                        <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">Email me aaya 6-digit code</label>
                        <input inputMode="numeric" maxLength={6} value={fOtp}
                          onChange={(e) => setFOtp(e.target.value.replace(/\D/g, ""))}
                          className="mt-2 w-full px-4 py-3.5 rounded-xl bg-white/[0.03] border border-white/10 text-white text-center text-2xl tracking-[0.5em] mono outline-none focus:border-cyan-400/50" />
                      </div>
                      <div>
                        <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">New password (at least 6)</label>
                        <input type="password" value={fPw} onChange={(e) => setFPw(e.target.value)}
                          className="mt-2 w-full px-4 py-3.5 rounded-xl bg-white/[0.03] border border-white/10 text-white outline-none focus:border-cyan-400/50" />
                      </div>
                      <div>
                        <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">New 4-digit PIN (optional)</label>
                        <input type="password" inputMode="numeric" maxLength={4} value={fPin}
                          onChange={(e) => setFPin(e.target.value.replace(/\D/g, ""))}
                          className="mt-2 w-full px-4 py-3.5 rounded-xl bg-white/[0.03] border border-white/10 text-white text-center tracking-[0.5em] mono outline-none focus:border-cyan-400/50" />
                      </div>
                      <button onClick={doReset} disabled={fBusy}
                        className="w-full py-3.5 rounded-xl bg-white text-black font-semibold text-[14px] disabled:opacity-60">
                        {fBusy ? "Changing…" : "Change password / PIN"}
                      </button>
                      <button onClick={sendOtp} disabled={fBusy}
                        className="w-full mono text-[11px] text-white/45 hover:text-cyan-300">Code didn't arrive? Resend</button>
                    </>
                  )}

                  {fMsg && <div className={`text-[13px] mono ${fMsg.startsWith("✅") ? "text-emerald-300" : "text-amber-300"}`}>{fMsg}</div>}
                  <button onClick={() => { setForgot(0); setFMsg(""); }}
                    className="w-full mono text-[11px] uppercase tracking-[0.24em] text-white/45 hover:text-white">← Back to login</button>
                </div>
              ) : adminTab === "password" ? (
                <form onSubmit={submitAdminPassword} className="space-y-4 relative">
                  <div data-book-selector className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-white/[0.03] border border-white/10">
                    <button type="button" onClick={() => { setAdminError(""); setErrHint(""); setBook("main"); try { localStorage.setItem("it_book", "main"); } catch (e) {} }}
                      className={"py-2 rounded-lg text-[13px] mono transition " + (book === "gst" ? "text-white/50 hover:text-white/70" : "bg-cyan-400/20 text-white")}>
                      Cash book
                    </button>
                    <button type="button" onClick={() => { setAdminError(""); setErrHint(""); setBook("gst"); try { localStorage.setItem("it_book", "gst"); } catch (e) {} }}
                      className={"py-2 rounded-lg text-[13px] mono transition " + (book === "gst" ? "bg-emerald-400/20 text-white" : "text-white/50 hover:text-white/70")}>
                      GST book
                    </button>
                  </div>
                  <div>
                    <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">Email or Mobile</label>
                    <input
                      data-testid={AUTH.emailInput}
                      type="text" value={email} onChange={(e) => setEmail(e.target.value)}
                      autoComplete="username" required
                      className="mt-2 w-full px-4 py-3.5 rounded-xl bg-white/[0.03] border border-white/10 text-white placeholder-white/30 outline-none focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/20 transition"
                    />
                  </div>
                    <p className="mt-1.5 text-[11px] text-white/45 mono">Hum aapke email ya WhatsApp par ek 6-digit code bhejenge.</p>
                  <label className="flex items-center gap-2 mono text-[12px] text-white/60 cursor-pointer" data-testid={AUTH.rememberDevice}>
                    <input type="checkbox" checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} className="w-4 h-4 accent-cyan-400" />
                    Remember this device
                  </label>
                  {adminError && (
                    <div className="rounded-xl px-3.5 py-3 bg-rose-400/10 border border-rose-400/25">
                      <div className="text-[13px] text-rose-300 mono">{adminError}</div>
                      {errHint && (
                        <div className="mono text-[10.5px] text-white/60 mt-1.5 leading-relaxed">{errHint}</div>
                      )}
                      {netFail && (
                        <div className="mono text-[10.5px] text-white/50 mt-2">
                          Is device ka waqt: <b className="text-white/75">{new Date().toLocaleString()}</b>
                        </div>
                      )}
                      <button type="button" onClick={hardReset}
                        className="mt-2.5 rounded-full px-3.5 py-1.5 text-[11.5px] glass hover:border-cyan-400/40">
                        🔄Remove the old copy and try again
                      </button>
                    </div>
                  )}
                  <button
                    data-testid={AUTH.loginSubmit}
                    type="submit" disabled={submitting}
                    className="w-full py-3.5 rounded-xl bg-white text-black font-semibold text-[14px] flex items-center justify-center gap-2 hover:bg-cyan-200 disabled:opacity-60"
                  >
                    {submitting ? "Sending code…" : "Send login code"}
                    <ArrowRight size={16} />
                  </button>
                  <button type="button" onClick={() => { setForgot(1); setFMsg(""); setAdminError(""); }}
                    className="w-full mono text-[11.5px] text-cyan-300/80 hover:text-cyan-300 pt-1">
                    Forgot password / PIN?
                  </button>
                </form>
              ) : (
                <form onSubmit={submitAdminPin} className="space-y-5 relative">
                  <div data-book-selector-pin className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-white/[0.03] border border-white/10">
                    <button type="button" onClick={() => { setAdminError(""); setErrHint(""); setBook("main"); try { localStorage.setItem("it_book", "main"); } catch (e) {} }}
                      className={"py-2 rounded-lg text-[13px] mono transition " + (book === "gst" ? "text-white/50 hover:text-white/70" : "bg-cyan-400/20 text-white")}>
                      Cash book
                    </button>
                    <button type="button" onClick={() => { setAdminError(""); setErrHint(""); setBook("gst"); try { localStorage.setItem("it_book", "gst"); } catch (e) {} }}
                      className={"py-2 rounded-lg text-[13px] mono transition " + (book === "gst" ? "bg-emerald-400/20 text-white" : "text-white/50 hover:text-white/70")}>
                      GST book
                    </button>
                  </div>
                  <div>
                    <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">Email</label>
                    <input
                      data-testid={AUTH.emailInput}
                      type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                      className="mt-2 w-full px-4 py-3.5 rounded-xl bg-white/[0.03] border border-white/10 text-white placeholder-white/30 outline-none focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/20 transition"
                    />
                  </div>
                  <div>
                    <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">4-digit PIN</label>
                    <input
                      data-testid={AUTH.pinInput}
                      type="password" inputMode="numeric" pattern="[0-9]{4}" maxLength={4}
                      value={adminPin}
                      onChange={(e) => {
                        const d = e.target.value.replace(/\D/g, "").slice(0, 4);
                        setAdminPin(d);
                        setAdminError("");
                        // 4 ank pure — apne aap login, Enter dabane ki zaroorat nahi
                        if (d.length === 4 && !submitting) setTimeout(() => submitAdminPinRef.current?.(d), 60);
                      }}
                      placeholder="••••" required
                      className="mt-2 w-full px-4 py-4 rounded-xl bg-white/[0.03] border border-white/10 text-white placeholder-white/30 outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 text-center tracking-[0.6em] mono text-xl"
                    />
                  </div>
                  {adminError && (
                    <div className="rounded-xl px-3.5 py-3 bg-rose-400/10 border border-rose-400/25">
                      <div className="text-[13px] text-rose-300 mono">{adminError}</div>
                      {errHint && (
                        <div className="mono text-[10.5px] text-white/60 mt-1.5 leading-relaxed">{errHint}</div>
                      )}
                      {netFail && (
                        <div className="mono text-[10.5px] text-white/50 mt-2">
                          Is device ka waqt: <b className="text-white/75">{new Date().toLocaleString()}</b>
                        </div>
                      )}
                      <button type="button" onClick={hardReset}
                        className="mt-2.5 rounded-full px-3.5 py-1.5 text-[11.5px] glass hover:border-cyan-400/40">
                        🔄Remove the old copy and try again
                      </button>
                    </div>
                  )}
                  <button
                    data-testid={AUTH.pinLoginSubmit}
                    type="submit" disabled={submitting}
                    className="w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-500 text-black font-semibold text-[14px] flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60"
                  >
                    {submitting ? "Verifying…" : "Unlock dashboard"} <Fingerprint size={16} />
                  </button>
                </form>
              )}

              <div className="mt-6 pt-5 border-t border-white/5 mono text-[10px] text-white/40 flex items-center gap-2">
                <ShieldCheck size={12} className="text-cyan-300" />
                End-to-end encrypted · Session timeout 12h
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
